import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import SearchResults from "@/pages/news/components/SearchResults";
import useSeo from "@/hooks/useSeo";
import { allNews } from "@/lib/content";

const CATEGORIES = ["全部", "影视资讯", "影视动态", "人物访谈"];
const SORTS = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "话题热度" },
];
const PAGE_SIZE = 5;

export default function NewsList() {
  useSeo({
    title: "筱田优影视资讯 - 最新动态与报道",
    description: "筱田优影视资讯，涵盖最新电影电视剧报道及动态消息。",
    keywords: "筱田优影视资讯,影视动态,电影报道,电视剧资讯",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [category, sort, keyword]);

  const filtered = useMemo(() => {
    const base = category === "全部" ? allNews : allNews.filter((n) => n.category === category);
    const sorted = [...base].sort((a, b) =>
      sort === "hot" ? b.hot - a.hot : b.date.localeCompare(a.date),
    );
    return sorted;
  }, [category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setSort("latest");
    setPage(1);
  };

  const clearSearch = () => {
    searchParams.delete("q");
    setSearchParams(searchParams);
  };

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: "影视资讯" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 mt-4">
            最新影视资讯
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl leading-relaxed">
            汇聚筱田优的最新影视动态、作品报道与人物访谈，追踪每一个值得关注的瞬间。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-12">
        {keyword ? (
          <SearchResults keyword={keyword} onClear={clearSearch} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-4 h-9 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      category === cat
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                {SORTS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setSort(item.key)}
                    className={`px-4 h-8 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sort === item.key
                        ? "bg-background-50 text-primary-600 border border-primary-300"
                        : "text-foreground-500 hover:text-foreground-800"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无相关资讯"
                description="当前筛选条件下还没有内容，试试其他分类吧。"
                onReset={resetFilters}
                actionTo="/"
                actionLabel="返回首页"
              />
            ) : (
              <>
                <div className="flex flex-col gap-4">
                  {pageItems.map((news) => (
                    <NewsCard key={news.id} news={news} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}